import type { FC, ReactNode } from 'react'
import type { Division, DivisionGroup } from '@/constants/event'
import type { SpotsResponse } from '@/lib/entries'

import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { Eyebrow } from '@/components/landing/eyebrow'
import { TeamEntryForm } from '@/components/landing/team-entry-form'
import { cn } from '@/lib/utils'
import { DIVISIONS, EVENT } from '@/constants/event'

const GROUPS: Array<{ group: DivisionGroup, title: string, note: string }> = [
    { group: 'Pro', title: 'Pro divisions', note: '$110,000 in prize money' },
    { group: 'Amateur', title: 'Amateur and senior divisions', note: 'Medals in every division' }
]

interface DivisionRowProps {
    division: Division
    entered: number | null
    selected: boolean
    onSelect: () => void
}

const DivisionRow: FC<DivisionRowProps> = ({ division, entered, selected, onSelect }): ReactNode => {
    const filled = entered === null ? 0 : Math.min(entered, division.draw)
    const left = division.draw - filled
    const full = entered !== null && left <= 0

    let availability = `Draw of ${division.draw} teams`
    if (entered !== null) availability = full ? 'Draw full, waitlist open' : `${left} of ${division.draw} spots left`

    return (
        <li>
            <button
                type='button'
                onClick={onSelect}
                aria-pressed={selected}
                className={cn(
                    'group grid w-full grid-cols-[1fr_auto] items-center gap-x-4 gap-y-3 rounded-2xl border px-5 py-4 text-left transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-court md:grid-cols-[1fr_11rem_5rem_auto] md:px-6',
                    selected ? 'border-ink bg-ink text-cream' : 'border-ink/12 bg-card hover:border-ink/40'
                )}
            >
                <span className='min-w-0'>
                    <span className='block font-heading text-2xl font-extrabold uppercase leading-none md:text-[1.7rem]'>
                        {division.name}
                    </span>
                    <span className={cn('mt-1.5 block text-sm', selected ? 'text-cream/70' : 'text-muted-foreground')}>
                        {`${division.eligibility} · ${division.prize}`}
                    </span>
                </span>

                <span className='col-span-2 row-start-2 md:col-span-1 md:row-start-auto'>
                    <span className={cn('block font-mono text-[10.5px] uppercase tracking-[0.14em]', selected ? 'text-cream/75' : 'text-ink/70')}>
                        {availability}
                    </span>
                    <span className={cn('mt-2 block h-1.5 overflow-hidden rounded-full', selected ? 'bg-cream/15' : 'bg-ink/10')}>
                        <span
                            className={cn('block h-full rounded-full transition-[width] duration-700', full ? 'bg-coral' : 'bg-lime-deep')}
                            style={{ width: `${(filled / division.draw) * 100}%` }}
                        />
                    </span>
                </span>

                <span className='font-heading text-2xl font-black md:text-right'>
                    {`$${division.fee}`}
                </span>

                <span
                    aria-hidden='true'
                    className={cn(
                        'hidden size-9 items-center justify-center rounded-full transition-colors md:inline-flex',
                        selected ? 'bg-lime text-ink' : 'bg-ink/5 text-ink group-hover:bg-ink group-hover:text-cream'
                    )}
                >
                    <ArrowUpRight className='size-4' />
                </span>
            </button>
        </li>
    )
}

export const PlaySection: FC = (): ReactNode => {
    const [division, setDivision] = useState(DIVISIONS[4].id)
    const [counts, setCounts] = useState<Record<string, number> | null>(null)
    const formRef = useRef<HTMLDivElement>(null)

    const loadCounts = useCallback(async (): Promise<void> => {
        try {
            const response = await fetch('/api/spots', { cache: 'no-store' })
            const result = (await response.json()) as SpotsResponse
            if (result.ok) setCounts(result.counts)
        }
        catch {
            // Spots are a nice-to-have: without them the list simply shows each draw size.
        }
    }, [])

    useEffect(() => {
        void loadCounts()
    }, [loadCounts])

    const chooseDivision = (id: string): void => {
        setDivision(id)

        const form = formRef.current
        if (!form) return

        if (window.matchMedia('(max-width: 1023px)').matches) {
            form.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }

        window.setTimeout(() => form.querySelector<HTMLInputElement>('input[name="captainName"]')?.focus({ preventScroll: true }), 400)
    }

    return (
        <section
            id='play'
            aria-labelledby='play-title'
            className='relative bg-cream text-ink'
        >
            <div className='mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-32'>
                <div className='flex flex-col justify-between gap-8 lg:flex-row lg:items-end'>
                    <div>
                        <Eyebrow className='text-court'>Play</Eyebrow>

                        <h2 id='play-title' className='mt-5 font-heading text-[clamp(3.2rem,8vw,7.5rem)] font-black uppercase leading-[0.86]'>
                            Enter a team.
                            <span className='serif-soft block font-serif font-normal normal-case italic text-court'>bring a partner.</span>
                        </h2>
                    </div>

                    <p className='max-w-md text-base leading-relaxed text-ink/75 lg:pb-3'>
                        {`Eight doubles divisions, from 3.5 to the pros. Entries are confirmed in the order they arrive and close on ${EVENT.entriesClose}, or sooner when a draw fills.`}
                    </p>
                </div>

                <div className='mt-14 grid gap-10 lg:mt-20 lg:grid-cols-12 lg:gap-12'>
                    <div className='flex flex-col gap-10 lg:col-span-7'>
                        {GROUPS.map(({ group, title, note }) => (
                            <div key={group}>
                                <div className='mb-4 flex items-baseline justify-between gap-4 border-b border-ink/15 pb-3'>
                                    <h3 className='font-mono text-xs font-medium uppercase tracking-[0.2em]'>
                                        {title}
                                    </h3>
                                    <p className='text-sm text-ink/60'>
                                        {note}
                                    </p>
                                </div>

                                <ul className='flex flex-col gap-2.5'>
                                    {DIVISIONS.filter((item) => item.group === group).map((item) => (
                                        <DivisionRow
                                            key={item.id}
                                            division={item}
                                            entered={counts ? counts[item.id] ?? 0 : null}
                                            selected={item.id === division}
                                            onSelect={() => chooseDivision(item.id)}
                                        />
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <div ref={formRef} className='scroll-mt-24 lg:col-span-5'>
                        <div className='rounded-[2rem] bg-ink p-6 text-cream shadow-[0_40px_80px_-40px_rgba(13,20,51,0.6)] md:p-9 lg:sticky lg:top-28'>
                            <p className='font-mono text-[11px] uppercase tracking-[0.2em] text-lime'>
                                Team entry
                            </p>

                            <p className='mb-7 mt-2 font-heading text-4xl font-extrabold uppercase leading-[0.92]'>
                                Two names, one form.
                            </p>

                            <TeamEntryForm
                                division={division}
                                onDivisionChange={setDivision}
                                onEntered={() => void loadCounts()}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
